import {
  ColumnFiltersState,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  OnChangeFn,
  SortingState,
  Table,
  TableOptions,
  TableState,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import { pickBy } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useIsMobile } from '../../../../hooks/use-is-mobile';
import { ColumnV2, TableV2LogicProps, TableV2MobileMode } from '../types';
import { customSortingFns } from './custom-sorting-fns';
import { useTableV2Export } from './export';
import { getPageSizeStateFromProp } from './pagination';

const DEFAULT_LOADING_ROWS = 10;

type TableV2Processed<T> = {
  table: Table<T>;
  handleTableExport: (() => void) | undefined;
};

export function useTableV2<T extends { subRows?: T[] }, ColumnId extends string>(
  props: TableV2LogicProps<T, ColumnId>,
  mobileMode: TableV2MobileMode | undefined,
): TableV2Processed<T> {
  const { isLoading } = props;
  const isMobile = useIsMobile();

  const columns = useMemo((): Array<ColumnV2<T>> => {
    if (!isLoading) return props.columns;
    return props.columns.map(c => {
      const childColumns =
        'columns' in c
          ? c.columns?.map(childColumn => {
              return {
                ...getSkeletonColumn(childColumn as ColumnV2<T>),
              };
            })
          : undefined;
      return {
        ...getSkeletonColumn(c),
        columns: childColumns,
      };
    });
  }, [props.columns, isLoading]);

  // Do any custom data transformation.
  const data = useMemo<T[]>(() => {
    if (isLoading) {
      // Fill the data with empty objects
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return Array(props.loadingRows || DEFAULT_LOADING_ROWS).fill({});
    } else {
      // If data is undefined, set it as an empty array
      return props.data || [];
    }
  }, [isLoading, props.loadingRows, props.data]);

  const initialState: Partial<TableState> = useMemo(() => {
    return {
      columnVisibility: columns.reduce((columnVisibility: VisibilityState | undefined, columnDef) => {
        const columnId = columnDef.id;
        let visible = true;
        if (columnDef.meta?.initiallyHidden) visible = false;
        if (isMobile && columnDef.meta?.hideOnMobile) visible = false;
        if (columnVisibility) {
          columnVisibility[columnId] = visible;
        } else {
          columnVisibility = { [columnId]: visible };
        }
        return columnVisibility;
      }, undefined),
      sorting: props.sorting
        ? props.sorting.sortingState
        : columns
            .filter(c => c.meta?.initiallySorted)
            .map(c => {
              return { id: c.id, desc: c.meta?.initiallySorted === 'DESC' };
            }),
      pagination: props.pagination
        ? props.pagination.paginationState
        : {
            pageIndex: 0,
            pageSize: getPageSizeStateFromProp(props.pageSize),
          },
      columnFilters: props.filtering?.filteringState || [],
      ...props.initialState,
    };
  }, [
    columns,
    props.sorting,
    props.pagination,
    props.pageSize,
    props.filtering?.filteringState,
    props.initialState,
    isMobile,
  ]);

  // Pagination is also added to the table state, see "stateProp" below
  const paginationProps: Pick<
    TableOptions<T>,
    'manualPagination' | 'onPaginationChange' | 'pageCount' | 'autoResetPageIndex'
  > = useMemo(
    () =>
      props.pagination
        ? {
            manualPagination: props.pagination.manualPagination ?? true,
            onPaginationChange: props.pagination.setPaginationState,
            pageCount: props.pagination.pageCount,
            // The caller owns the page, so new data (a poll, a deleted row) must not move it.
            autoResetPageIndex: false,
          }
        : {},
    [props.pagination],
  );
  const pageSize = getPageSizeStateFromProp(props.pageSize);
  const enablePaginating = props.pagination ? !paginationProps.manualPagination : pageSize > 0;

  // Sorting is also added to the table state, see "stateProp" below
  const sortingProps: Pick<TableOptions<T>, 'manualSorting' | 'onSortingChange'> = useMemo(
    () =>
      props.sorting
        ? {
            manualSorting: props.sorting.manualSorting,
            // Our typed prop is more strict with column names than Tanstack Table's generic string function, so we'll do some assertion
            // here to suppress the type error of not being able to pass a generic string into a more strict stype.
            onSortingChange: props.sorting.onSortingChange as unknown as OnChangeFn<SortingState>,
          }
        : {},
    [props.sorting],
  );

  const filteringProps: Pick<TableOptions<T>, 'manualFiltering' | 'onColumnFiltersChange'> = useMemo(() => {
    return props.filtering
      ? {
          manualFiltering: !!props.filtering.onColumnFiltersChange,
          // Our typed prop is more strict with column names than Tanstack Table's generic string function, so we'll do some assertion
          // here to suppress the type error of not being able to pass a generic string into a more strict stype.
          onColumnFiltersChange: props.filtering.onColumnFiltersChange as unknown as OnChangeFn<ColumnFiltersState>,
        }
      : {};
  }, [props.filtering]);

  // Call the react-table library with the modified data and column definitions
  const table = useReactTable<T>({
    ...paginationProps,
    ...sortingProps,
    ...filteringProps,
    data,
    columns,
    initialState,
    enableColumnFilters: !isLoading,
    sortingFns: customSortingFns,
    enableSorting: !props.disableSort,
    enableSortingRemoval: false,
    autoResetAll: props.autoReset,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: !sortingProps.manualSorting ? getSortedRowModel() : undefined,
    getPaginationRowModel: enablePaginating ? getPaginationRowModel() : undefined,
    getFilteredRowModel: !filteringProps.manualFiltering ? getFilteredRowModel() : undefined,
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: row => row.subRows,
    meta: { mobileMode },
  });

  const [tableState, setTableState] = useState<TableState>(table.initialState);

  const statePossiblyControlledByProps: TableOptions<T>['state'] = useMemo(
    () =>
      pickBy(
        {
          pagination: props.pagination?.paginationState,
          sorting: props.sorting?.sortingState,
          columnFilters: props.filtering?.filteringState,
        },
        value => value !== undefined,
      ),
    [props.filtering?.filteringState, props.pagination?.paginationState, props.sorting?.sortingState],
  );

  table.setOptions(prevOptions => ({
    ...prevOptions,
    state: { ...tableState, ...statePossiblyControlledByProps },
    onStateChange: setTableState,
  }));

  // Whenever the number of filtered rows changes, we broadcast the new length using given prop.
  // This allows parent component to display the number of matched rows even when using internal filtering.
  const filteredRowCount = table.getFilteredRowModel().rows.length || 0;
  const setNumberOfMatchedRows = props.onMatchedCountChange;
  useEffect(() => {
    if (setNumberOfMatchedRows) {
      setNumberOfMatchedRows(filteredRowCount);
    }
  }, [setNumberOfMatchedRows, filteredRowCount]);

  const handleTableExport = useTableV2Export(columns, table, {
    enableExport: props.enableExport,
    filename: props.exportFilename,
  });

  useEffect(() => {
    // Force the visibility state to reload on column definition change
    // (to emulate "autoResetVisibilityState")
    if ((props.autoResetVisibilityState || props.autoReset) && initialState.columnVisibility) {
      setTableState(oldState => ({ ...oldState, columnVisibility: initialState.columnVisibility! }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.autoReset, props.autoResetVisibilityState, columns]);

  return {
    table,
    handleTableExport,
  };
}

function getSkeletonColumn<T extends object>(column: ColumnV2<T, any>) {
  return {
    ...column,
    id: column.id,
    accessorFn: undefined,
    accessorKey: undefined,
    cell: () => {
      return (
        <div style={{ width: '100%', zIndex: 0 }}>
          <Skeleton width={'65%'} />
        </div>
      );
    },
  };
}
